export interface CustomerSummary {
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  ticketCount: number;
  totalSpent: number;
  firstVisit: string;
  lastVisit: string;
  workshopCount: number;
}

export interface CustomerListResponse {
  customers: CustomerSummary[];
  summary: {
    totalCustomers: number;
    totalRevenue: number;
    avgSpend: number;
    repeatCustomerCount: number;
  };
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
}

export interface CustomerTicket {
  id: string;
  workshopId: string;
  workshopTitle: string;
  workshopDate: string;
  venue: string;
  venueCity: string;
  species: string;
  region: string;
  status: string;
  ticketType: string;
  pricePaid: number;
  customerCount: number;
  fareharborDisplayId: string | null;
  fareharborDashboardUrl: string | null;
  source: string;
  payments: {
    amountPaid: number;
    status: string;
    type: string;
    refunds: {
      amountRefunded: number;
      isCancelled: boolean;
      refundedAt: string | null;
    }[];
  }[];
}

export interface ShopifyLineItem {
  title: string;
  quantity: number;
  price: number;
  productType: string | null;
  sku: string | null;
}

export interface CustomerShopifyOrder {
  id: string;
  orderNumber: string;
  orderDate: string;
  totalPrice: number;
  subtotalPrice: number;
  totalTax: number;
  totalDiscounts: number;
  financialStatus: string;
  fulfillmentStatus: string | null;
  cancelledAt: string | null;
  refundTotal: number;
  lineItems: ShopifyLineItem[];
}

export interface CustomerDetail {
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  dateOfBirth: string | null;
  referralSource: string | null;
  customerSince: string | null;
  notes: string | null;
  stats: {
    totalSpent: number;
    totalRefunded: number;
    netSpent: number;
    workshopCount: number;
    ticketCount: number;
    cancelledCount: number;
    avgTicketPrice: number;
    avgGroupSize: number;
    shopifyTotalSpent: number;
    combinedTotalSpent: number;
  };
  tickets: CustomerTicket[];
  shopifyOrders: CustomerShopifyOrder[];
}

export interface FetchCustomersParams {
  search?: string;
  sort?: string;
  sortDir?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

import { apiFetch } from '../lib/apiFetch';

export async function fetchCustomers(params: FetchCustomersParams = {}): Promise<CustomerListResponse> {
  const qs = new URLSearchParams();
  if (params.search) qs.set('search', params.search);
  if (params.sort) qs.set('sort', params.sort);
  if (params.sortDir) qs.set('sortDir', params.sortDir);
  if (params.limit) qs.set('limit', params.limit.toString());
  if (params.offset !== undefined) qs.set('offset', params.offset.toString());

  const res = await apiFetch(`/api/v1/customers?${qs.toString()}`);
  if (!res.ok) {
    throw new Error(`Failed to fetch customers: ${res.status}`);
  }
  return res.json();
}

export async function fetchCustomerDetail(email: string): Promise<CustomerDetail> {
  const res = await apiFetch(`/api/v1/customers/${encodeURIComponent(email)}`);
  if (!res.ok) {
    throw new Error(`Failed to fetch customer: ${res.status}`);
  }
  const data = await res.json();
  return data.customer;
}
